import { useMemo } from 'react';

import { Breakpoint } from '@/hooks/use-breakpoints';

import JobCard from '@/components/job-card';
import Link, { LinkVariant } from '@/components/link';
import Typography from '@/components/typography';

import NOMAD_API_CONSTANTS from '@/constants/nomad-api';
import Carousel, {
  CarouselItem,
  CarouselPerPage,
  CarouselVariant,
} from '@/features/carousel';
import { JobBlocksCardCarouselProps } from '@/features/job-blocks/types';
import { routes } from '@/utils/route-utils';

export default function JobBlocksCardCarousel({
  title,
  jobs,
  discipline,
  specialty,
}: JobBlocksCardCarouselProps) {
  const cards: CarouselItem[] = useMemo(
    () =>
      jobs.map((job) => ({
        id: job.id,
        component: <JobCard key={job.id} {...job} full />,
      })),
    [jobs]
  );

  if (!jobs || jobs.length < 4) return null;

  const specialtyTitle = specialty?.title;
  const disciplineName =
    specialty?.discipline?.title || discipline?.title || 'Nurse';
  const disciplineTitle = disciplineName?.match(/travel/i)
    ? disciplineName
    : `Travel ${disciplineName}`;

  const carouselTitle =
    title ||
    (specialtyTitle
      ? `Popular ${specialtyTitle} ${disciplineTitle} Jobs`
      : `Popular ${disciplineTitle} Jobs`);

  const searchAllRedirectPath = routes.jobSearch({
    ...NOMAD_API_CONSTANTS.DEFAULT_JOB_SEARCH_QUERY,
    specialty: specialty?.apiName || undefined,
    discipline: discipline?.apiId || undefined,
  });

  return (
    <div className='w-full flex flex-col gap-2 rounded-2xl bg-light-purple p-8'>
      <Typography.Heading2>{carouselTitle}</Typography.Heading2>

      <Carousel
        variant={CarouselVariant.PAGINATED}
        items={cards}
        showPagination
        perPage={{
          [Breakpoint.sm]: CarouselPerPage.ONE,
          [Breakpoint.md]: CarouselPerPage.ONE,
          [Breakpoint.lg]: CarouselPerPage.TWO,
          [Breakpoint.xl]: CarouselPerPage.TWO,
          [Breakpoint.xxl]: CarouselPerPage.TWO,
        }}
      />

      <div className='mb-4 flex items-center justify-center'>
        <Link variant={LinkVariant.PRIMARY_BUTTON} href={searchAllRedirectPath}>
          View All
        </Link>
      </div>
    </div>
  );
}
